<template>
  <b-card no-body>
    <b-card-header class="d-flex flex-wrap align-items-center py-1 history-card-header" style="gap:6px;">
      <h5 class="mb-0 flex-shrink-0">
        <feather-icon icon="ListIcon" size="16" class="mr-50" />
        Call History
        <b-badge
          v-if="dateRangeLabel"
          variant="light-primary"
          class="ml-50"
        >{{ dateRangeLabel }}</b-badge>
        <b-badge
          variant="light-secondary"
          class="ml-50"
        >{{ historyCountBadge }}</b-badge>
      </h5>
      <b-form-input
        :value="searchQuery"
        size="sm"
        debounce="350"
        class="flex-shrink-0 history-search-input"
        placeholder="Search name, phone, email…"
        aria-label="Search calls"
        @input="$emit('search-change', $event)"
      />
      <b-form-select
        :value="analysisFilter || 'all'"
        :options="analysisFilterOptions"
        size="sm"
        class="flex-shrink-0 history-analysis-filter ml-auto"
        aria-label="Filter Call History table"
        @change="$emit('analysis-filter-change', $event)"
      />
      <b-form-select
        :value="perPage"
        :options="[10, 15, 25, 50, 100]"
        size="sm"
        class="flex-shrink-0"
        style="width: auto;"
        @change="$emit('update:perPage', Number($event) || 50)"
      />
    </b-card-header>

    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="primary" />
      <p class="mt-1 text-muted">{{ loadingMessage }}</p>
    </div>

    <div v-else-if="tableItems.length === 0" class="text-center py-4 text-muted">
      <feather-icon icon="PhoneOffIcon" size="32" class="mb-1 text-muted" />
      <p class="mb-0">{{ emptyStateTitle }}</p>
      <p v-if="emptyStateDetail" class="small mt-50 mb-0 px-2">{{ emptyStateDetail }}</p>
    </div>

    <div
      v-else
      class="calls-table-shell"
    >
    <b-table
      striped
      hover
      responsive
      :primary-key="'rowKey'"
      :items="tableItems"
      :fields="displayFields"
      :per-page="0"
      sort-by="date"
      :sort-desc="true"
      :sort-compare="historySortCompare"
      no-local-sorting
      show-empty
      class="mb-0 calls-table"
    >
      <!-- Date, time, then In/Out · Fresh/Follow-up chip on its own line so it never overflows -->
      <template #cell(date)="data">
        <div class="date-cell" :title="compactDateTime(data.item)">
          <span class="date-cell__date">{{ historyDateLabel(data.item) }}</span>
          <span v-if="historyTimeLabel(data.item)" class="date-cell__time">{{ historyTimeLabel(data.item) }}</span>
          <b-badge
            :variant="directionBadgeVariant(getDisplayDirection(data.item))"
            class="date-cell__chip"
            :title="historyChipTitle(data.item)"
          >
            {{ historyChipLabel(data.item) }}
          </b-badge>
        </div>
      </template>

      <!-- Candidate & Job: wrapped name + SHC, wrapping job line, recruiter on its own line -->
      <template #cell(candidateAndJob)="data">
        <div class="call-details-cell" :title="getCallDetailsMetaTitle(data.item)">
          <div class="call-details-primary">
            <b-button
              v-if="canDial(data.item)"
              variant="outline-success"
              size="sm"
              class="p-25 call-details-dial"
              :aria-label="`Call ${data.item.phone}`"
              @click="$emit('call-candidate', data.item)"
            >
              <feather-icon icon="PhoneIcon" size="13" />
            </b-button>

            <div class="call-details-identity">
              <template v-if="data.item.candidateId">
                <router-link
                  v-if="data.item.jobId"
                  :to="{ name: 'apps-candidates-view', params: { id: data.item.candidateId, jobId: data.item.jobId } }"
                  class="call-detail-link call-details-name"
                  :title="getCandidateName(data.item)"
                >{{ getCandidateName(data.item) }}</router-link>
                <span
                  v-else
                  class="call-details-name"
                  :title="getCandidateName(data.item)"
                >{{ getCandidateName(data.item) }}</span>
                <small
                  v-if="data.item.candidateIdDisplay"
                  class="call-details-id text-monospace"
                  :title="data.item.candidateIdDisplay"
                >{{ data.item.candidateIdDisplay }}</small>
              </template>

              <template v-else>
                <span
                  class="call-details-name"
                  :title="canDial(data.item) ? data.item.phone : 'Unknown caller — no dialable number'"
                >{{ canDial(data.item) ? data.item.phone : 'Unknown caller' }}</span>
                <b-badge variant="light-danger" class="call-details-ats-pill">
                  <feather-icon icon="UserXIcon" size="10" class="mr-25" />Not in ATS
                </b-badge>
              </template>
            </div>
          </div>

          <div v-if="data.item.jobId" class="call-details-job">
            <span v-if="getJobClient(data.item)">{{ getJobClient(data.item) }}</span>
            <template v-if="getJobClient(data.item) && getJobPosition(data.item) !== '—'"> · </template>
            <router-link
              :to="{ name: 'apps-view-job', params: { id: data.item.jobId } }"
              class="call-detail-link font-weight-bold"
            >{{ getJobPosition(data.item) }}</router-link>
          </div>
          <div v-if="getCandidateStatus(data.item)" class="call-details-status">
            <b-badge variant="light-secondary" class="call-details-status-pill">
              {{ getCandidateStatus(data.item) }}
            </b-badge>
          </div>

          <div
            v-if="showRecruiter && getRecruiterName(data.item) !== '—'"
            class="call-details-recruiter"
          >
            <feather-icon icon="UserIcon" size="12" class="call-details-meta-icon" />{{ getRecruiterName(data.item) }}
          </div>
          <div
            v-else-if="shouldShowReceivedBy(data.item)"
            class="call-details-recruiter"
          >
            <feather-icon icon="PhoneIncomingIcon" size="12" class="call-details-meta-icon" />{{ getRecruiterName(data.item) }}
          </div>
        </div>
      </template>

      <!-- Call Status: full-word pill + duration -->
      <template #cell(status)="data">
        <div class="call-status-cell">
          <b-badge :variant="data.item.statusVariant" class="call-status-pill">
            {{ data.item.status }}
          </b-badge>
          <b-badge
            v-if="data.item._callsAvailableSkip"
            variant="light-secondary"
            class="call-status-away"
          >Received while away</b-badge>
          <span class="call-status-duration">{{ data.item.duration || '—' }}</span>
          <button
            v-if="callNotes[getCallSid(data.item)]"
            type="button"
            class="call-status-note"
            @click="$emit('open-note', data.item)"
          >
            <feather-icon icon="EyeIcon" size="12" class="mr-25" />View Note
          </button>
        </div>
      </template>

      <!-- Hygiene -->
      <template #cell(hygiene)="data">
        <template v-if="hasCompletedAnalysis(data.item)">
          <div class="d-flex align-items-center hygiene-cell" style="gap:6px;">
            <div class="hygiene-bar-track flex-grow-1">
              <div
                class="hygiene-bar-fill"
                :style="{
                  width: hygienePercent(data.item) + '%',
                  background: hygieneScoreColor(getHygieneScore(getAnalysis(data.item))),
                }"
              />
            </div>
            <span class="small text-nowrap">{{ formatScorePercent(getHygieneScore(getAnalysis(data.item))) }}</span>
          </div>
        </template>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Quality -->
      <template #cell(quality)="data">
        <template v-if="hasCompletedAnalysis(data.item)">
          <div class="analysis-badge-cell d-flex flex-column align-items-start" style="gap:4px;">
            <b-badge
              v-if="getEngagementLabel(data.item)"
              :variant="engagementBadgeVariant(getEngagementLevel(data.item))"
              pill
              class="analysis-badge font-weight-normal"
            >
              {{ getEngagementLabel(data.item) }}
            </b-badge>
            <b-badge
              :variant="flagsBadgeVariant(getRedFlagCount(getAnalysis(data.item)))"
              pill
              class="analysis-badge font-weight-normal"
            >
              {{ getFlagsLabel(data.item) }}
            </b-badge>
          </div>
        </template>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Interest -->
      <template #cell(interest)="data">
        <b-badge
          v-if="hasCompletedAnalysis(data.item)"
          :variant="interestBadgeVariant(getInterestLevel(data.item))"
          pill
          class="analysis-badge font-weight-normal"
        >
          {{ formatInterestLabel(getAnalysis(data.item)) }}
        </b-badge>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Outcome -->
      <template #cell(outcome)="data">
        <b-badge
          v-if="hasCompletedAnalysis(data.item)"
          :variant="outcomeBadgeVariant(getOutcomeCategory(data.item))"
          pill
          class="analysis-badge font-weight-normal"
        >
          {{ formatOutcomeLabel(getAnalysis(data.item)) }}
        </b-badge>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- AI Analysis -->
      <template #cell(analysis)="data">
        <b-button
          v-if="canViewCallAnalysis(data.item)"
          variant="flat-secondary"
          size="sm"
          class="btn-icon p-50"
          aria-label="View AI Analysis"
          @click.stop="openAnalysis(data.item)"
        >
          <feather-icon icon="CpuIcon" size="15" />
        </b-button>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Actions: status indicators + More dropdown -->
      <template #cell(actions)="data">
        <div class="d-flex align-items-center justify-content-center" style="gap:4px;">

          <feather-icon
            v-if="hasInterestStatusMismatch(data.item)"
            icon="AlertTriangleIcon"
            size="15"
            class="text-danger flex-shrink-0"
            aria-label="Interest status mismatch"
          />

          <!-- Calling spinner -->
          <b-button
            v-if="callingRowId === data.item.rowKey"
            variant="flat-secondary"
            size="sm"
            class="p-50"
            disabled
          >
            <b-spinner small style="width:.8rem;height:.8rem;" />
            <span class="ml-25 small">Calling…</span>
          </b-button>

          <!-- Post-dial outcome (matches dialer) -->
          <span
            v-else-if="getCallOutcome(data.item) === 'initiated'"
            class="d-inline-flex align-items-center small text-success font-weight-bold"
          >
            <feather-icon icon="CheckCircleIcon" size="13" class="mr-25" />
            Initiated
          </span>
          <span
            v-else-if="getCallOutcome(data.item) === 'connected'"
            class="d-inline-flex align-items-center small text-success font-weight-bold"
          >
            <feather-icon icon="PhoneCallIcon" size="13" class="mr-25" />
            Connected
          </span>
          <span
            v-else-if="getCallOutcome(data.item) === 'not_answered'"
            class="d-inline-flex align-items-center small text-warning font-weight-bold"
          >
            <feather-icon icon="PhoneOffIcon" size="13" class="mr-25" />
            Not Answered
          </span>
          <span
            v-else-if="getCallOutcome(data.item) === 'failed'"
            class="d-inline-flex align-items-center small text-danger font-weight-bold mr-25"
          >
            <feather-icon icon="XCircleIcon" size="13" class="mr-25" />
            Failed
          </span>

          <!-- Actions dropdown -->
          <b-dropdown
            variant="flat-secondary"
            size="sm"
            no-caret
            right
            class="p-0"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="15" />
            </template>

            <!-- Play recording -->
            <b-dropdown-item
              v-if="hasRecording(data.item)"
              @click="openRecording(data.item)"
            >
              <feather-icon icon="PlayCircleIcon" size="13" class="mr-50 text-info" />
              Play Recording
            </b-dropdown-item>

            <!-- Download recording -->
            <b-dropdown-item
              v-if="hasRecording(data.item)"
              @click="downloadRecording(data.item)"
            >
              <feather-icon icon="DownloadIcon" size="13" class="mr-50 text-primary" />
              Download Recording
            </b-dropdown-item>

            <!-- Call again -->
            <b-dropdown-item
              v-if="canDial(data.item) && !['queued','in-progress'].includes(data.item._rawStatus) && !getCallOutcome(data.item) && callingRowId !== data.item.rowKey"
              link-class="text-success"
              @click="$emit('call-again', data.item)"
            >
              <feather-icon icon="PhoneIcon" size="13" class="mr-50" />
              Call {{ data.item.phone }}
            </b-dropdown-item>

            <!-- Tag / Note -->
            <b-dropdown-item @click="$emit('open-note', data.item)">
              <feather-icon icon="BookmarkIcon" size="13" class="mr-50" />
              {{ callNotes[getCallSid(data.item)] ? 'Edit Note' : 'Add Note / Tag' }}
            </b-dropdown-item>

            <!-- Add to ATS (only when candidate not in system) -->
            <b-dropdown-item
              v-if="!data.item.candidateId"
              @click="$emit('open-add-to-ats', data.item)"
            >
              <feather-icon icon="UserPlusIcon" size="13" class="mr-50" />
              Add to ATS
            </b-dropdown-item>

            <!-- Set Reminder (no-answer only) -->
            <b-dropdown-item v-if="isNoAnswer(data.item)" @click="$emit('open-reminder', data.item)">
              <feather-icon icon="BellIcon" size="13" class="mr-50" />
              Set Callback Reminder
            </b-dropdown-item>

          </b-dropdown>

        </div>
      </template>
    </b-table>
    </div>

    <b-card-footer
      v-if="!isLoading && (paginationTotal > perPage || isFetchingMore)"
      class="py-1"
    >
      <b-pagination
        v-if="paginationTotal > perPage"
        :value="currentPage"
        :total-rows="paginationTotal"
        :per-page="perPage"
        align="center"
        class="mb-0"
        @input="$emit('update:currentPage', $event)"
      />
      <div
        v-if="isFetchingMore"
        class="d-flex flex-wrap align-items-center justify-content-center mt-1"
        style="gap:8px;"
      >
        <b-spinner small variant="secondary" />
        <small class="text-muted">Loading page…</small>
      </div>
      <small
        v-else
        class="d-block text-center text-muted mt-50"
      >
        Page {{ currentPage }}
        · {{ Number(tableItems.length).toLocaleString('en-IN') }}
        on this page
        <template v-if="badgeTotalCount">
          · {{ Number(badgeTotalCount).toLocaleString('en-IN') }} total
        </template>
      </small>
    </b-card-footer>

    <!-- Recording Player Modal -->
    <b-modal
      v-model="recordingModal.show"
      title="Call Recording"
      hide-footer
      centered
      size="md"
      @hidden="onRecordingModalHidden"
    >
      <div class="py-1">
        <div v-if="recordingModal.loading" class="py-3 text-center text-muted small">
          <b-spinner small class="mr-50" />
          Loading recording…
        </div>
        <div v-else-if="recordingModal.error" class="py-2 text-center text-danger small">
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-50" />
          {{ recordingModal.error }}
        </div>
        <audio
          v-else-if="isPlayableRecordingUrl(recordingModal.url)"
          controls
          autoplay
          style="width:100%; outline:none;"
          :src="recordingModal.url"
        >
          Your browser does not support audio playback.
        </audio>
        <div v-else class="py-2 text-center text-muted small">
          <feather-icon icon="MicOffIcon" size="16" class="mr-50" />
          No recording available for this call.
        </div>
      </div>
    </b-modal>

    <!-- AI Analysis Detail Modal -->
    <call-analysis-detail-modal
      v-model="analysisModal.show"
      :call="analysisModal.call"
      view-mode="history"
      @hidden="analysisModal.call = null"
    />
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardFooter,
  BTable,
  BButton,
  BBadge,
  BSpinner,
  BModal,
  BPagination,
  BFormSelect,
  BFormInput,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { isDialableIndianPhone, isTelephonyConnectedCall } from '@/utils/exotel-call-helpers'
import { hasRecordingAvailable, isValidRecordingUrl } from '@/utils/call-recording-helpers'
import {
  canViewCallAnalysis,
  hasInterestStatusMismatch,
  hasCompletedAnalysis,
  parseAnalysis,
  resolveRecruiterDisplayName,
  getDisplayDirection,
  directionBadgeVariant,
  getHygieneScore,
  hygieneScoreColor,
  formatScorePercent,
  formatInterestLabel,
  formatOutcomeLabel,
  getCallQuality,
  getRedFlags,
  getRedFlagCount,
  formatQualityLevel,
  engagementBadgeVariant,
  flagsBadgeVariant,
  interestBadgeVariant,
  outcomeBadgeVariant,
  pickExplicitCallRowDate,
  parseCallRowTimestamp,
  formatHistoryDateIst,
  sortCallsNewestFirst,
  callRowSortTimestamp,
  callRowSortId,
} from '@/utils/call-analysis-helpers'
import callAnalyticsService from '@/services/call-analytics-service'
import { resolveCallCandidateStatus } from '../utils/call-filter-scope'
import CallAnalysisDetailModal from './CallAnalysisDetailModal.vue'

const ANALYSIS_FIELD_KEYS = ['hygiene', 'quality', 'interest', 'outcome', 'analysis']

export default {
  name: 'MyCallsHistoryTable',

  components: {
    BCard,
    BCardHeader,
    BCardFooter,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BModal,
    BPagination,
    BFormSelect,
    BFormInput,
    BDropdown,
    BDropdownItem,
    CallAnalysisDetailModal,
  },

  props: {
    filteredCalls: { type: Array, required: true },
    /**
     * Badge/display total. When analysis filter is client-only this is the
     * matched-on-page count (not the unfiltered API total).
     */
    serverTotal: { type: Number, default: 0 },
    /** Raw API `totalRecords` — drives page navigation when filter is client-only. */
    apiTotal: { type: Number, default: 0 },
    /** Matching rows on the currently fetched API page (after Call Analysis filter). */
    loadedCount: { type: Number, default: 0 },
    /** True when more API pages exist (parent `hasMoreCalls`). */
    hasMore: { type: Boolean, default: false },
    isFetchingMore: { type: Boolean, default: false },
    fields: { type: Array, required: true },
    isLoading: { type: Boolean, default: false },
    perPage: { type: Number, default: 50 },
    currentPage: { type: Number, default: 1 },
    /** Filters-modal Call Analysis (all | analyzed | not_analyzed). Toolbar is table-only. */
    analysisFilter: { type: String, default: 'all' },
    /** True when parent paginates over the live period set (search or analysis filter). */
    clientPaged: { type: Boolean, default: false },
    /** Analyzed count across the selected period (live set when loaded). */
    periodAnalyzedCount: { type: Number, default: null },
    /** Total calls across the selected period (live set when loaded). */
    periodTotalCount: { type: Number, default: null },
    /** True while full-period analyzed/total counts are still loading. */
    periodCountsLoading: { type: Boolean, default: false },
    /**
     * Legacy: backend ignored analyzed= on the current API page only.
     * Superseded by clientPaged full-period filtering when the live set is loaded.
     */
    analysisFilterClientOnly: { type: Boolean, default: false },
    callingRowId: { default: null },
    callOutcomeByRowKey: { type: Object, default: () => ({}) },
    callNotes: { type: Object, default: () => ({}) },
    candidateMap: { type: Object, default: () => ({}) },
    jobMap: { type: Object, default: () => ({}) },
    recruiterNameMap: { type: Object, default: () => ({}) },
    showRecruiter: { type: Boolean, default: false },
    /** Applied date window, e.g. "Last 7 days" — not the All calls analysis dropdown. */
    dateRangeLabel: { type: String, default: '' },
    /** History search (name / phone / email) — parent matches across the full period set. */
    searchQuery: { type: String, default: '' },
  },

  data() {
    return {
      recordingModal: {
        show: false,
        url: null,
        error: null,
        loading: false,
      },
      analysisModal: {
        show: false,
        call: null,
      },
    }
  },

  computed: {
    usesClientPagination() {
      return this.clientPaged
    },

    loadingMessage() {
      return 'Loading calls…'
    },

    /** Rows in the current table scope (after parent KPI / date / scope filters). */
    totalCallsCount() {
      return Array.isArray(this.filteredCalls) ? this.filteredCalls.length : 0
    },

    badgeTotalCount() {
      if (this.usesClientPagination) {
        return this.scopedCalls.length
      }
      const server = Number(this.serverTotal) || 0
      if (server > 0) return server
      const api = Number(this.apiTotal) || 0
      if (api > 0) return api
      const loaded = Number(this.loadedCount) || 0
      if (loaded > 0) return loaded
      return this.totalCallsCount
    },

    paginationTotal() {
      if (this.usesClientPagination) {
        return this.scopedCalls.length
      }
      const server = Number(this.serverTotal) || 0
      if (server > 0) return server
      const api = Number(this.apiTotal) || 0
      if (api > 0) return api
      return this.scopedCalls.length
    },

    analysisFilterOptions() {
      return [
        { value: 'all', text: 'All calls' },
        { value: 'analyzed', text: 'Analyzed only' },
        { value: 'not_analyzed', text: 'Not analyzed' },
      ]
    },

    historyCountBadge() {
      const fmt = n => Number(n || 0).toLocaleString('en-IN')
      const shown = this.tableItems.length
      const total = this.badgeTotalCount
      if (this.isLoading && !shown && !total) return 'Loading…'
      return `showing ${fmt(shown)} of ${fmt(total)}`
    },

    emptyStateTitle() {
      if (this.analysisFilter === 'analyzed') {
        return 'No analyzed calls in this date range.'
      }
      if (this.analysisFilter === 'not_analyzed') {
        return 'No answered calls without analysis in this date range.'
      }
      return 'No calls found for the selected filters.'
    },

    emptyStateDetail() {
      if (this.analysisFilter === 'analyzed' && this.scopedCalls.length === 0) {
        return 'No completed AI analysis for the selected date range and filters.'
      }
      if (this.analysisFilter === 'not_analyzed' && this.scopedCalls.length === 0) {
        return 'Every answered call in this date range already has completed AI analysis.'
      }
      return ''
    },

    /** Parent already sent one API page (or a KPI chip set). Do not re-filter analyzed here. */
    scopedCalls() {
      return Array.isArray(this.filteredCalls) ? this.filteredCalls : []
    },

    /** Client-side page slice only for KPI chip lists; History is one API page. */
    tableItems() {
      const filtered = sortCallsNewestFirst(this.scopedCalls)
      const pageSize = Math.max(1, Number(this.perPage) || 50)
      if (this.usesClientPagination) {
        const start = (Math.max(1, Number(this.currentPage) || 1) - 1) * pageSize
        return filtered.slice(start, start + pageSize)
      }
      if (filtered.length > pageSize) return filtered.slice(0, pageSize)
      return filtered
    },

    /** Always include Hygiene / Quality / Interest / Outcome / Analysis (dashes when empty). */
    displayFields() {
      const dateStyle = { width: '210px', minWidth: '210px', maxWidth: '210px' }
      const detailsStyle = { width: '270px', minWidth: '220px', maxWidth: '270px' }
      const statusStyle = { width: '100%', minWidth: '128px', maxWidth: 'none' }
      const scoreStyle = { width: '100%', minWidth: '120px', maxWidth: 'none' }
      const analysisIconStyle = { width: '100%', minWidth: '110px', maxWidth: 'none' }
      const actionsStyle = { width: '64px', minWidth: '64px', maxWidth: '64px' }
      const styleFor = key => {
        if (key === 'date') return dateStyle
        if (key === 'candidateAndJob') return detailsStyle
        if (key === 'status') return statusStyle
        if (key === 'analysis') return analysisIconStyle
        if (ANALYSIS_FIELD_KEYS.includes(key)) return scoreStyle
        if (key === 'actions') return actionsStyle
        return null
      }
      return (Array.isArray(this.fields) ? this.fields : []).map(f => {
        if (!f) return f
        const style = styleFor(f.key)
        if (!style) return f
        const next = {
          ...f,
          thClass: String(f.thClass || '')
            .replace(/\bd-none\b/g, '')
            .replace(/\bd-lg-table-cell\b/g, '')
            .replace(/\s+/g, ' ')
            .trim(),
          tdClass: String(f.tdClass || '')
            .replace(/\bd-none\b/g, '')
            .replace(/\bd-lg-table-cell\b/g, '')
            .replace(/\s+/g, ' ')
            .trim(),
          thStyle: { ...(f.thStyle || {}), ...style },
          tdStyle: { ...(f.tdStyle || {}), ...style },
        }
        if (f.key === 'actions' || f.key === 'analysis') {
          next.thClass = [next.thClass, 'text-center'].filter(Boolean).join(' ')
          next.tdClass = [next.tdClass, 'text-center'].filter(Boolean).join(' ')
        }
        return next
      })
    },
  },

  watch: {
    analysisFilter(val, oldVal) {
      if (val === oldVal) return
      this.$emit('update:currentPage', 1)
    },
  },

  methods: {
    getCandidateName(item) {
      if (item.candidateId && this.candidateMap[item.candidateId]?.name) {
        return this.candidateMap[item.candidateId].name
      }
      return item.candidate && item.candidate !== '—' ? item.candidate : '—'
    },

    getJobPosition(item) {
      if (item.jobId && this.jobMap[item.jobId]?.position) {
        return this.jobMap[item.jobId].position
      }
      return item.job || item.jobCode || '—'
    },

    getJobClient(item) {
      if (item.clientName) return item.clientName
      if (item.jobId && this.jobMap[item.jobId]?.client) {
        return this.jobMap[item.jobId].client
      }
      return ''
    },

    getCandidateStatus(item) {
      const entry = item && item.candidateId && this.candidateMap
        ? this.candidateMap[item.candidateId]
        : null
      const jobs = entry && Array.isArray(entry.candidateJobs) ? entry.candidateJobs : []
      let match = null
      if (jobs.length && item && item.jobId != null) {
        const jobIdStr = String(item.jobId)
        match = jobs.find(j => (
          String(j.id) === jobIdStr
          || String(j.job_id) === jobIdStr
          || String(j.jobId) === jobIdStr
        )) || null
      }
      return resolveCallCandidateStatus(item, match)
    },

    getJobCode(item) {
      if (item.jobId && this.jobMap[item.jobId]?.code) {
        return this.jobMap[item.jobId].code
      }
      return item.jobCode || (item.jobId ? `SHJ-${item.jobId}` : '—')
    },

    getRecruiterName(item) {
      return resolveRecruiterDisplayName(item._rawRecruiter, this.recruiterNameMap, item._recruiterName)
    },

    getCallDetailsMetaTitle(item) {
      const parts = []
      const client = this.getJobClient(item)
      const position = this.getJobPosition(item)
      if (item.jobId) {
        if (client && position && position !== '—') parts.push(`${client} · ${position}`)
        else if (client) parts.push(client)
        else if (position && position !== '—') parts.push(position)
      }
      if (this.showRecruiter && this.getRecruiterName(item) !== '—') {
        parts.push(this.getRecruiterName(item))
      } else if (this.shouldShowReceivedBy(item)) {
        parts.push(`Received by: ${this.getRecruiterName(item)}`)
      }
      const status = this.getCandidateStatus(item)
      if (status) parts.push(status)
      return parts.join(' · ')
    },

    shouldShowReceivedBy(item) {
      if (this.showRecruiter) return false
      if (getDisplayDirection(item) !== 'Inbound') return false
      return this.getRecruiterName(item) !== '—'
    },

    historyDateParts(item) {
      let ts = Number(item && item._ts)
      if (!ts) {
        const fallback = (item && item._rawDate) || pickExplicitCallRowDate(item)
        const parsed = parseCallRowTimestamp(fallback)
        if (parsed) ts = parsed.getTime()
      }
      if (ts) {
        return formatHistoryDateIst(new Date(ts))
      }
      return { date: '—', time: '' }
    },

    historySortCompare(a, b, key) {
      if (key === 'date') {
        const tb = callRowSortTimestamp(b)
        const ta = callRowSortTimestamp(a)
        if (tb !== ta) return ta - tb
        return callRowSortId(a) - callRowSortId(b)
      }
      return false
    },

    historyDateLabel(item) {
      return this.historyDateParts(item).date
    },

    historyTimeLabel(item) {
      return this.historyDateParts(item).time
    },

    compactDateTime(item) {
      const parts = this.historyDateParts(item)
      if (parts.date && parts.time) return `${parts.date}, ${parts.time}`
      return parts.date || '—'
    },

    historyChipLabel(item) {
      const dir = this.getDisplayDirection(item) === 'Inbound' ? 'In' : 'Out'
      const rawType = item && item.callTypeTag && item.callTypeTag.label
      const type = rawType === 'Fresh Call' ? 'Fresh' : (rawType || '')
      return type ? `${dir} · ${type}` : dir
    },

    historyChipTitle(item) {
      const dir = this.getDisplayDirection(item)
      const type = item && item.callTypeTag && item.callTypeTag.label
      return type ? `${dir} · ${type}` : dir
    },

    isPlayableRecordingUrl(url) {
      return isValidRecordingUrl(url)
    },

    getCallSid(item) {
      return item.sid || item.rowKey || `${item.candidateId || ''}_${item.jobId || ''}`
    },

    isNoAnswer(item) {
      return ['no-answer', 'no_answer', 'noanswered', 'noanswer', 'not answered', 'notanswered'].includes(item._rawStatus)
    },

    canDial(item) {
      if (!item?.phone || item.phone === '—') return false
      if (item.phoneDialable === true) return true
      if (item.phoneDialable === false) return false
      return isDialableIndianPhone(item.phone)
    },

    getCallOutcome(item) {
      if (!item?.rowKey) return null
      const outcome = this.callOutcomeByRowKey[item.rowKey] || null
      if (!outcome) return null
      // Post-dial "Not Answered" is a transient dial attempt label — never overlay it on
      // a row Exotel already recorded as connected (e.g. re-dial from an answered log row).
      if (outcome === 'not_answered' && isTelephonyConnectedCall(item)) return null
      if (isTelephonyConnectedCall(item) && outcome !== 'initiated') return null
      return outcome
    },

    openRecording(item) {
      this.recordingModal.show = true
      this.loadRecordingIntoModal(item)
    },

    onRecordingModalHidden() {
      this.recordingModal.url = null
      this.recordingModal.error = null
      this.recordingModal.loading = false
    },

    hasRecording(item) {
      return hasRecordingAvailable(item)
    },

    async resolveRecordingUrl(item) {
      return callAnalyticsService.resolveRecordingPlayUrl({
        recordingS3Key: item.recordingS3Key || null,
        sid: item.sid || null,
        callDbId: item.callDbId || null,
        item,
      })
    },

    async loadRecordingIntoModal(item) {
      this.recordingModal.loading = true
      this.recordingModal.url = null
      this.recordingModal.error = null
      try {
        const res = await this.resolveRecordingUrl(item)
        if (res?.status === 'OK' && isValidRecordingUrl(res.url)) {
          this.recordingModal.url = res.url
          return
        }
        this.recordingModal.url = null
        this.recordingModal.error = res?.message || 'No recording available for this call.'
      } catch (err) {
        console.error('[MyCallsHistoryTable] recording load failed:', err)
        this.recordingModal.error = 'Could not load recording. Please try again.'
      } finally {
        this.recordingModal.loading = false
      }
    },

    async downloadRecording(item) {
      try {
        const filename = `call-${item.sid || item.callDbId || 'recording'}.mp3`

        if (item.recordingS3Key) {
          const res = await callAnalyticsService.getRecordingFileBase64(item.recordingS3Key)
          if (res?.status === 'OK' && res.file) {
            const dataUrl = `data:audio/mpeg;base64,${res.file}`
            const link = document.createElement('a')
            link.href = dataUrl
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            return
          }

          this.$bvToast.toast(
            res?.message || 'Could not download recording from S3.',
            { title: 'Download Failed', variant: 'warning', solid: true },
          )
          return
        }

        const res = await this.resolveRecordingUrl(item)
        if (res?.status !== 'OK' || !isValidRecordingUrl(res.url)) {
          this.$bvToast.toast(
            res?.message || 'No recording available for this call.',
            { title: 'Download Failed', variant: 'warning', solid: true },
          )
          return
        }

        const link = document.createElement('a')
        link.href = res.url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (err) {
        console.error('[MyCallsHistoryTable] recording download failed:', err)
        this.$bvToast.toast(
          'Could not download recording. Please try again.',
          { title: 'Download Failed', variant: 'danger', solid: true },
        )
      }
    },

    canViewCallAnalysis,
    hasInterestStatusMismatch,
    hasCompletedAnalysis,

    getDisplayDirection,
    directionBadgeVariant,
    getHygieneScore,
    hygieneScoreColor,
    formatScorePercent,
    formatInterestLabel,
    formatOutcomeLabel,
    engagementBadgeVariant,
    flagsBadgeVariant,
    interestBadgeVariant,
    outcomeBadgeVariant,
    getCallQuality,
    getRedFlags,
    getRedFlagCount,

    getAnalysis(item) {
      return parseAnalysis(item)
    },

    hygienePercent(item) {
      const score = getHygieneScore(parseAnalysis(item))
      if (score == null || Number.isNaN(Number(score))) return 0
      return Math.round(Number(score) * 100)
    },

    getEngagementLabel(item) {
      const engagement = this.getEngagementLevel(item)
      if (!engagement) return null
      return formatQualityLevel(engagement)
    },

    getEngagementLevel(item) {
      const quality = getCallQuality(parseAnalysis(item))
      return quality && quality.candidate_engagement ? quality.candidate_engagement : null
    },

    getInterestLevel(item) {
      const analysis = parseAnalysis(item)
      return analysis && analysis.candidate_interest ? analysis.candidate_interest.level : null
    },

    getOutcomeCategory(item) {
      const analysis = parseAnalysis(item)
      return analysis && analysis.call_outcome ? analysis.call_outcome.category : null
    },

    getFlagsLabel(item) {
      const flagCount = getRedFlagCount(parseAnalysis(item))
      if (flagCount) return `${flagCount} flag${flagCount === 1 ? '' : 's'}`
      return 'No flags'
    },

    /** Opens the modal only — full analysis is fetched there (API 2), not assumed on the list row. */
    openAnalysis(item) {
      if (!item || !canViewCallAnalysis(item)) return
      this.analysisModal.call = item
      this.analysisModal.show = true
    },
  },
}
</script>

<style scoped>
.calls-table-shell,
.calls-table-shell >>> .table-responsive {
  width: 100%;
  overflow-x: auto;
}

.calls-table,
.calls-table >>> table {
  table-layout: fixed;
  width: 100%;
  min-width: 1180px;
}

.calls-table >>> thead th {
  white-space: nowrap;
  word-break: keep-all;
  overflow-wrap: normal;
}

.calls-table >>> th,
.calls-table >>> td {
  box-sizing: border-box;
  vertical-align: middle;
  padding-left: 0.65rem;
  padding-right: 0.65rem;
}

.calls-table >>> td {
  padding-top: 13px;
  padding-bottom: 13px;
}

.calls-table >>> tbody tr:hover,
.calls-table >>> tbody tr:hover > td {
  background-color: #e4e9f6 !important;
}

.calls-table >>> th.date,
.calls-table >>> td.date {
  width: 192px !important;
  min-width: 192px !important;
  max-width: 192px !important;
  padding-left: 0.75rem;
  padding-right: 0.65rem;
  white-space: normal;
  overflow: visible;
}

.calls-table >>> th.status,
.calls-table >>> td.status {
  width: 100% !important;
  min-width: 128px !important;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}

.calls-table >>> th.hygiene,
.calls-table >>> td.hygiene,
.calls-table >>> th.quality,
.calls-table >>> td.quality,
.calls-table >>> th.interest,
.calls-table >>> td.interest,
.calls-table >>> th.outcome,
.calls-table >>> td.outcome {
  width: 100% !important;
  min-width: 120px !important;
  max-width: none !important;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.calls-table >>> th.analysis,
.calls-table >>> td.analysis {
  width: 100% !important;
  min-width: 110px !important;
  max-width: none !important;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.calls-table >>> th.hygiene,
.calls-table >>> th.quality,
.calls-table >>> th.interest,
.calls-table >>> th.outcome,
.calls-table >>> th.analysis {
  white-space: nowrap;
  word-break: keep-all;
}

.calls-table >>> th.actions,
.calls-table >>> td.actions {
  width: 64px !important;
  min-width: 64px !important;
  max-width: 64px !important;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  white-space: nowrap;
  text-align: center;
}

.date-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  max-width: 100%;
}

.date-cell__date,
.date-cell__time {
  font-size: 13px;
  line-height: 1.25;
  color: #4b465c;
  white-space: nowrap;
}

.date-cell__date {
  font-weight: 600;
}

.date-cell__time {
  font-weight: 400;
}

.date-cell__chip {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 7px;
  line-height: 1.3;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}

.calls-table >>> th.candidateAndJob,
.calls-table >>> td.candidateAndJob {
  width: 270px !important;
  min-width: 220px !important;
  max-width: 270px !important;
  white-space: normal;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.call-details-cell {
  min-width: 0;
  max-width: 100%;
  width: 100%;
  line-height: 1.35;
}

.call-details-primary {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
}

.call-details-identity {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 6px;
  min-width: 0;
  flex: 1 1 auto;
}

.call-details-dial {
  flex-shrink: 0;
}

.call-details-name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  font-size: 16px;
  font-weight: 600;
  color: #0099ac;
}

.call-details-id {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  font-size: 13px;
  line-height: 1.25;
  color: #5e5873;
}

.call-details-ats-pill {
  flex: 0 1 auto;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  line-height: 1.25;
  white-space: nowrap;
}

.call-details-status {
  display: block;
  margin-top: 3px;
  max-width: 100%;
}

.call-details-status-pill {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  line-height: 1.25;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.call-details-job,
.call-details-recruiter {
  display: block;
  margin-top: 3px;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.35;
  color: #5e5873;
}

.call-details-meta-icon {
  margin-right: 3px;
  vertical-align: -0.12em;
  color: #5e5873;
}

.call-status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.call-status-pill {
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  line-height: 1.25;
  white-space: nowrap;
  overflow: visible;
  max-width: none;
}

.call-status-away {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  line-height: 1.25;
  white-space: nowrap;
}

.call-status-duration {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  color: #4b465c;
}

.call-status-note {
  padding: 0;
  border: 0;
  background: none;
  color: #7367f0;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

.analysis-badge {
  font-size: 12px;
}

.hygiene-bar-track {
  background: #ebe9f1;
  border-radius: 4px;
  height: 6px;
  overflow: hidden;
  min-width: 56px;
}

.hygiene-bar-fill {
  height: 6px;
  border-radius: 4px;
  transition: width 0.4s ease;
}

.hygiene-cell {
  min-width: 48px;
}

.history-analysis-filter {
  width: auto;
  min-width: 140px;
}

.history-search-input {
  width: 220px;
  max-width: 100%;
}

@media (max-width: 576px) {
  .history-card-header {
    row-gap: 8px;
  }
  .history-search-input {
    order: 3;
    width: 100%;
  }
  .history-analysis-filter {
    order: 4;
    width: 100%;
    min-width: 0;
  }
}

.call-detail-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  border-radius: 3px;
}

.call-detail-link.call-details-name {
  color: #0099ac;
}

.call-detail-link:hover {
  color: inherit;
  text-decoration: none;
  background: rgba(0, 0, 0, 0.04);
}

.call-detail-link.call-details-name:hover {
  color: #007a8a;
}
</style>
